import { baseApi } from 'services/api';
import { API_BASE_URL, API_TAG_TYPES, API_VERSION, JOB_MANAGER_API_HEADERS } from 'constants/common';
import {
  CreateTagPayload,
  CreateTagResponse,
  GetTagByIdPayload,
  GetTagByIdResponse,
  GetTagsPayload,
  GetTagsResponse,
  UpdateTagPayload,
  UpdateTagStatusPayload
} from './tags.model';
import { setNextDisplayOrder } from './tagsSlice';

const TagsBaseApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getTags: builder.query<GetTagsResponse, GetTagsPayload>({
      query: ({ teamId, isActive, page, limit }) => {
        const url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/tags`;
        return {
          url: url,
          method: 'GET',
          params: { isActive, page, limit },
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const { data: tags } = await queryFulfilled;
        const getLastDisplayOrder = tags?.data?.tags.reduce((acc, tag) => {
          return Math.max(acc, Number(tag.priority));
        }, 0);
        dispatch(setNextDisplayOrder(getLastDisplayOrder + 1));
      },
      providesTags: [API_TAG_TYPES.TAGS]
    }),
    getTagById: builder.query<GetTagByIdResponse, GetTagByIdPayload>({
      query: ({ teamId, tagId }) => {
        const url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/tags/${tagId}`;
        return {
          url: url,
          method: 'GET',
          headers: JOB_MANAGER_API_HEADERS
        };
      }
    }),
    createTag: builder.mutation<CreateTagResponse, CreateTagPayload>({
      query: ({ teamId, body }) => {
        const url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/tags`;
        return {
          url: url,
          method: 'POST',
          body: body,
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      invalidatesTags: [API_TAG_TYPES.TAGS],
      async onQueryStarted({ teamId }, { dispatch, queryFulfilled }) {
        const { data: newTag } = await queryFulfilled;

        dispatch(
          TagsBaseApi.util.updateQueryData('getTags', { teamId: Number(teamId) }, draft => {
            if (newTag?.data) {
              if (draft.data.tags) {
                draft.data.tags.push(newTag.data);
              } else {
                draft.data.tags = [newTag.data];
              }
            }
          })
        );
      },
      extraOptions: {
        showToast: true,
        success: 'Tag created successfully',
        failure: 'Failed to create tag'
      }
    }),
    updateTag: builder.mutation<CreateTagResponse, UpdateTagPayload>({
      query: ({ teamId, tagId, body }) => {
        const url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/tags/${tagId}`;
        return {
          url: url,
          method: 'PUT',
          body: body,
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      invalidatesTags: [API_TAG_TYPES.TAGS],
      extraOptions: {
        showToast: true,
        success: 'Tag updated successfully',
        failure: 'Failed to update tag'
      },
      async onQueryStarted({ teamId, tagId }, { dispatch, queryFulfilled }) {
        const { data: updatedTag } = await queryFulfilled;

        dispatch(
          TagsBaseApi.util.updateQueryData('getTags', { teamId: Number(teamId) }, draft => {
            const tagIndex = draft.data.tags?.findIndex(tag => Number(tag.id) === Number(tagId));

            if (tagIndex !== -1 && tagIndex !== undefined) {
              draft.data.tags[tagIndex] = updatedTag.data;
            }
          })
        );
      }
    }),
    updateTagStatus: builder.mutation<GetTagsResponse, UpdateTagStatusPayload>({
      query: ({ teamId, tagId, isActive }) => {
        const url = `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/merchants/teams/${teamId}/tags/${tagId}/active`;
        return {
          url: url,
          method: 'PATCH',
          body: { isActive },
          headers: JOB_MANAGER_API_HEADERS
        };
      },
      invalidatesTags: [API_TAG_TYPES.TAGS],
      async onQueryStarted({ teamId, tagId, isActive }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          TagsBaseApi.util.updateQueryData('getTags', { teamId }, draft => {
            const tagToUpdate = draft.data.tags.find(tag => tag.id === tagId);
            if (tagToUpdate) {
              tagToUpdate.isActive = isActive;
            }
          })
        );
      },
      extraOptions: {
        showToast: true,
        success: 'Tag status updated successfully',
        failure: 'Failed to update tag status'
      }
    })
  })
});

export const {
  useLazyGetTagsQuery,
  useGetTagByIdQuery,
  useLazyGetTagByIdQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useUpdateTagStatusMutation,
  useGetTagsQuery
} = TagsBaseApi;
